import React from 'react';
import { css } from 'emotion';
import { useQueryClient } from 'react-query';
import { connect } from 'react-redux';
import reduxForm, { InjectedFormProps } from 'redux-form/lib/reduxForm';
import Field from 'redux-form/lib/Field';
import SubmissionError from 'redux-form/lib/SubmissionError';

import { INTERACTIVE_QUESTION_KEY } from 'hooks/useInteractivityPolls';
import { AnchorAPI } from 'modules/AnchorAPI';
import { ListenerQuestion } from 'modules/AnchorAPI/v3/episodes/fetchListenerQuestion';

import { FieldInputWithRedux } from 'components/FieldInput';
import {
  ConfirmationButtons,
  ErrorMessage,
  StyledFieldLabel,
} from '../InteractivePoll/styles';
import { SubmitButton } from './SubmitButton';

type Props = {
  episodeId: string;
  onSuccess: () => void;
};

function QuestionCreatorFormView({
  handleSubmit,
  error,
  change,
  values,
  submitting,
  pristine,
  episodeId,
  onSuccess,
}: Props & StateProps & InjectedFormProps<ListenerQuestion, Props>) {
  const queryClient = useQueryClient();
  const onSubmit = async (formValues: ListenerQuestion) => {
    const { text } = formValues;
    try {
      await AnchorAPI.createListenerQuestion(episodeId, text);
      onSuccess();
      return queryClient.invalidateQueries([
        INTERACTIVE_QUESTION_KEY,
        episodeId,
      ]);
    } catch (e) {
      throw new SubmissionError({ _error: e.message });
    }
  };
  return (
    <form>
      <StyledFieldLabel htmlFor="text">Ask a question</StyledFieldLabel>
      <Field name="text" id="text" component={FieldInputWithRedux} />
      <div
        className={css`
          margin-top: 1rem;
        `}
      >
        <ConfirmationButtons>
          <SubmitButton
            isSubmitting={submitting}
            pristine={pristine}
            onClick={handleSubmit(onSubmit)}
          />
        </ConfirmationButtons>
      </div>
      {error && (
        <ErrorMessage>
          Sorry, looks like something went wrong. Try again?
        </ErrorMessage>
      )}
    </form>
  );
}

type StateProps = { values: ListenerQuestion };

function mapStateToProps(state: any): StateProps {
  return {
    values: state.form.createEpisodeQuestion.values,
  };
}

function validate(values: Partial<ListenerQuestion>) {
  type Object = Record<string, any>;
  const errors: Object = {};
  if (!values.text) {
    errors.text = 'Required';
  }
  return errors;
}

export const InteractiveQuestionCreateForm = reduxForm<ListenerQuestion, Props>(
  {
    form: 'createEpisodeQuestion',
    validate,
  }
)(connect(mapStateToProps)(QuestionCreatorFormView));
